import type { OptionCategoryData } from "@hitit/api"
import { TranslationDTO } from "../TranslationDTO";
import OptionItem from "./OptionItem";

export default class OptionCategory {

    translationDTOs: TranslationDTO[]
    enabled: boolean;
    displaySort: number;
    taxPercent: number;
    taxable: boolean;
    priceCurrency: string;
    price: number;
    inventory: number;
    takeoutalbe: boolean;
    deliverable: boolean;
    sellableInStore: boolean;
    sellableOnline: boolean;
    preOrderable: boolean;
    validFromDate: string;
    validUntil?: string;
    optionCollectionItemDTO: OptionItem[];
    private id?: string;
    uid?: string;
    mandatory: boolean;
    optionType: string;
    internalName: string;

    static convinienceConstructor(name: string, displaySort: number) {
        const arg: OptionCategoryData = {
            id: `|TEMP|${Math.random()}`,
            enabled: true,
            taxPercent: 10,
            price: 0,
            internalName: name,
            mandatory: false,
            inventory: 0,
            translationDTOs: [new TranslationDTO(name)],
            displaySort,
            taxable: true,
            priceCurrency: "KRW",
            takeoutalbe: false,
            deliverable: false,
            sellableInStore: false,
            sellableOnline: false,
            preOrderable: false,
            validFromDate: (new Date()).toISOString(),
            validUntil: undefined,
            optionCollectionItemDTO: [],
            optionType: "SINGLESELECT",
        }

        return new OptionCategory(arg)
    }


    constructor(data: OptionCategoryData) {
        this.uid = data.uid
        this.translationDTOs = data.translationDTOs;
        this.id = data.id;
        this.enabled = data.enabled;
        this.displaySort = data.displaySort;
        this.taxPercent = data.taxPercent;
        this.taxable = data.taxable;
        this.priceCurrency = data.priceCurrency
        this.price = data.price
        this.inventory = data.inventory
        this.deliverable = data.deliverable;
        this.sellableInStore = data.sellableInStore
        this.preOrderable = data.preOrderable
        this.validFromDate = data.validFromDate
        this.validUntil = data.validUntil
        this.takeoutalbe = data.takeoutalbe
        this.sellableOnline = data.sellableOnline
        this.mandatory = data.mandatory
        this.optionType = data.optionType
        this.internalName = data.internalName
        this.optionCollectionItemDTO = data.optionCollectionItemDTO
            .map((v) => new OptionItem(v, this))
            .sort((a, b) => a.displaySort - b.displaySort)
    }

    public resorting() {
        this.optionCollectionItemDTO = this.optionCollectionItemDTO
            .sort((a, b) => a.displaySort - b.displaySort)
        return this
    }

    public setIds(id: string, uid?: string) {
        this.id = id;
        this.uid = uid;
    }

    public getOptions() {
        return [...this.optionCollectionItemDTO]
    }

    public getStoreItemLength() {
        return this.optionCollectionItemDTO.length;
    }

    public setOption(option: OptionItem) {
        this.optionCollectionItemDTO =
            [...this.optionCollectionItemDTO.filter(v => v.id !== option.id), option]
                .sort((a, b) => a.displaySort - b.displaySort)
    }

    public findOptionByuid(uid: string) {
        return this.optionCollectionItemDTO.find((v) => v.uid === uid)
    }

    public getId() {
        return this.id
    }

    public setTranslations(translationDTOs: TranslationDTO[]) {
        this.translationDTOs = translationDTOs
    }

    public getTranslations() {
        return this.translationDTOs.map((v) => {
            const { name, id, description, defaultLang, lang } = v;
            return new TranslationDTO(name, lang, defaultLang, id, description)
        })
    }

    public deleteOption(id: string) {
        this.optionCollectionItemDTO = this.optionCollectionItemDTO.filter((v) => v.id !== id);
    }

    public getLastDisplaysort() {
        return this.optionCollectionItemDTO.reduce((prev, cur) => {
            if (prev < cur.displaySort) {
                return cur.displaySort
            }
            return prev
        }, 0)
    }

    public getOptionCategoryDTO() {
        const reg = /^\|TEMP\|/;
        const dto: OptionCategoryData = {
            translationDTOs: this.translationDTOs,
            enabled: this.enabled,
            displaySort: this.displaySort,
            taxPercent: this.taxPercent,
            taxable: this.taxable,
            priceCurrency: this.priceCurrency,
            price: this.price,
            inventory: this.inventory,
            takeoutalbe: this.takeoutalbe,
            deliverable: this.deliverable,
            sellableInStore: this.sellableInStore,
            sellableOnline: this.sellableOnline,
            preOrderable: this.preOrderable,
            validFromDate: this.validFromDate,
            validUntil: this.validUntil,
            optionCollectionItemDTO: this.optionCollectionItemDTO.map(v => v.getOptionCollectionItemDTO()),
            mandatory: this.mandatory,
            optionType: this.optionType,
            internalName: this.internalName,
            id: reg.test(this.id!) ? undefined : this.id,
            uid: reg.test(this.id!) ? undefined : this.uid
        }
        return dto
    }

    public sortOptionItem() {
        return this.optionCollectionItemDTO.sort((a, b) => a.displaySort - b.displaySort)
    }

    public updateDisplaysort(oldIndex: number, newIndex: number) {
        const optionCollectionItemDTO = this.optionCollectionItemDTO
        if (optionCollectionItemDTO[oldIndex] === undefined) {
            return optionCollectionItemDTO.map((v, i) => ({
                id: v.id || '',
                newDisplaySort: i
            }))
        }
        if (oldIndex < newIndex) {
            optionCollectionItemDTO.splice(newIndex + 1, 0, this.optionCollectionItemDTO[oldIndex]);
            optionCollectionItemDTO.splice(oldIndex, 1);
        } else {
            optionCollectionItemDTO.splice(newIndex, 0, this.optionCollectionItemDTO[oldIndex]);
            optionCollectionItemDTO.splice(oldIndex + 1, 1);
        }

        const updatedItems: { id: string, newDisplaySort: number }[] = [];

        for (let i = 0; i < optionCollectionItemDTO.length; i++) {
            const target = optionCollectionItemDTO[i];
            const updatedItem = {
                id: target.id || "",
                newDisplaySort: i
            }


            target.displaySort = i;
            updatedItems.push(updatedItem);
        }
        this.optionCollectionItemDTO = optionCollectionItemDTO
        this.sortOptionItem()
        return updatedItems
    }
    public updateMultipleDisplaySort(oldIndexes: number[], newIndexes: number[]) {
        const optionCollectionItemDTO = this.optionCollectionItemDTO
        const moveItem = optionCollectionItemDTO.filter(
            (_, i) => oldIndexes.find((v) => v === i) !== undefined
        )
        const filtered = optionCollectionItemDTO.filter((_, i) =>
            oldIndexes.find((v) => v === i) === undefined
        )

        filtered.splice(newIndexes[0], 0, ...moveItem);
        const newOptionItems = filtered
        const updatedItems: { id: string, newDisplaySort: number }[] = [];

        for (let i = 0; i < newOptionItems.length; i++) {
            const target = newOptionItems[i];
            const updatedItem = {
                id: target.id || "",
                newDisplaySort: i
            }


            target.displaySort = i;
            updatedItems.push(updatedItem);
        }
        this.optionCollectionItemDTO = newOptionItems
        this.resorting()

        return updatedItems
    }

}